import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  Chip,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Skeleton,
  Divider,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Alert
} from '@mui/material';
import { Close, Business, People, Description, AccountBalance, Person, CheckCircle, Cancel } from '@mui/icons-material';
import { superAdminService } from '../../../services/superAdmin/superAdminService';

const StatBox = ({ icon: Icon, label, value, color = '#14b8a6' }) => (
  <Box sx={{
    display: 'flex',
    alignItems: 'center',
    gap: 1.5,
    p: 2,
    borderRadius: 2,
    border: '1px solid #e2e8f0',
    backgroundColor: '#fafafa'
  }}>
    <Icon sx={{ color, fontSize: 20 }} />
    <Box>
      <Typography sx={{ fontSize: '0.7rem', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: '1.1rem', fontWeight: 700, color: '#0f172a' }}>
        {value}
      </Typography>
    </Box>
  </Box>
);

const PLAN_OPTIONS = [
  { value: 'business', label: 'Business', color: 'default' },
  { value: 'businessPlus', label: 'Business Plus', color: 'primary' },
  { value: 'premium', label: 'Premium', color: 'warning' },
];

const CompanyDetailDialog = ({ companyId, open, onClose }) => {
  const queryClient = useQueryClient();
  const [planUpdateMsg, setPlanUpdateMsg] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['superAdminCompanyDetail', companyId],
    queryFn: async () => {
      const result = await superAdminService.getCompanyDetail(companyId);
      if (!result.success) throw new Error(result.message);
      return result.data;
    },
    enabled: !!companyId
  });

  const planMutation = useMutation({
    mutationFn: ({ id, planId }) => superAdminService.updateCompanyPlan(id, planId),
    onSuccess: (result) => {
      setPlanUpdateMsg(result.message || 'Plan updated successfully');
      queryClient.invalidateQueries({ queryKey: ['superAdminCompanyDetail', companyId] });
      queryClient.invalidateQueries({ queryKey: ['superAdminCompanies'] });
    },
    onError: () => {
      setPlanUpdateMsg('Failed to update plan');
    }
  });

  const company = data || {};

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Business sx={{ color: '#14b8a6' }} />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {isLoading ? <Skeleton width={200} /> : company.name}
          </Typography>
          {!isLoading && (
            <Chip
              label={company.isActive ? 'Active' : 'Inactive'}
              color={company.isActive ? 'success' : 'default'}
              size="small"
            />
          )}
        </Box>
        <IconButton onClick={onClose}><Close /></IconButton>
      </DialogTitle>

      <DialogContent>
        {isLoading ? (
          <Box>
            <Grid container spacing={2} sx={{ mb: 2 }}>
              {[...Array(4)].map((_, i) => (
                <Grid item xs={6} sm={3} key={i}>
                  <Skeleton variant="rounded" height={70} />
                </Grid>
              ))}
            </Grid>
            <Skeleton variant="rounded" height={200} />
          </Box>
        ) : (
          <Box>
            {/* Usage Stats */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={6} sm={3}>
                <StatBox icon={People} label="Users" value={company.users?.length || 0} color="#3b82f6" />
              </Grid>
              <Grid item xs={6} sm={3}>
                <StatBox icon={Description} label="Cheques" value={company.chequeCount || 0} color="#10b981" />
              </Grid>
              <Grid item xs={6} sm={3}>
                <StatBox icon={AccountBalance} label="Banks" value={company.bankCount || 0} color="#f59e0b" />
              </Grid>
              <Grid item xs={6} sm={3}>
                <StatBox icon={Person} label="Beneficiaries" value={company.beneficiaryCount || 0} color="#8b5cf6" />
              </Grid>
            </Grid>

            <Typography sx={{ fontSize: '0.75rem', color: '#94a3b8', mb: 0.5 }}>
              Templates: {company.templateCount || 0} | Created: {company.createdDate ? new Date(company.createdDate).toLocaleDateString() : 'N/A'}
            </Typography>

            <Divider sx={{ my: 2 }} />

            {/* Plan Override */}
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
              Subscription Plan
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel>Plan</InputLabel>
                <Select
                  value={company.planId || 'business'}
                  label="Plan"
                  onChange={(e) => {
                    setPlanUpdateMsg('');
                    planMutation.mutate({ id: companyId, planId: e.target.value });
                  }}
                  disabled={planMutation.isPending}
                >
                  {PLAN_OPTIONS.map((plan) => (
                    <MenuItem key={plan.value} value={plan.value}>{plan.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Chip
                label={PLAN_OPTIONS.find(p => p.value === (company.planId || 'business'))?.label || 'Business'}
                color={PLAN_OPTIONS.find(p => p.value === (company.planId || 'business'))?.color || 'default'}
                size="small"
              />
              {planMutation.isPending && (
                <Typography variant="caption" color="text.secondary">Updating...</Typography>
              )}
            </Box>
            {planUpdateMsg && (
              <Alert
                severity={planMutation.isError ? 'error' : 'success'}
                sx={{ mb: 2, borderRadius: 2 }}
                onClose={() => setPlanUpdateMsg('')}
              >
                {planUpdateMsg}
              </Alert>
            )}

            <Divider sx={{ my: 2 }} />

            {/* Users Table */}
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
              Users ({company.users?.length || 0})
            </Typography>
            <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f8fafc' }}>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Name</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Email</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Role</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Verified</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(company.users || []).map((user) => (
                    <TableRow key={user.id}>
                      <TableCell sx={{ fontSize: '0.8rem' }}>{user.firstName} {user.lastName}</TableCell>
                      <TableCell sx={{ fontSize: '0.8rem' }}>{user.email}</TableCell>
                      <TableCell>
                        <Chip label={user.role} size="small" sx={{ fontSize: '0.7rem', height: 22 }} />
                      </TableCell>
                      <TableCell>
                        {user.isActive ? (
                          <CheckCircle sx={{ color: '#10b981', fontSize: 18 }} />
                        ) : (
                          <Cancel sx={{ color: '#ef4444', fontSize: 18 }} />
                        )}
                      </TableCell>
                      <TableCell>
                        {user.emailVerified ? (
                          <CheckCircle sx={{ color: '#10b981', fontSize: 18 }} />
                        ) : (
                          <Cancel sx={{ color: '#f59e0b', fontSize: 18 }} />
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                  {(!company.users || company.users.length === 0) && (
                    <TableRow>
                      <TableCell colSpan={5} align="center">
                        <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                          No users found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default CompanyDetailDialog;
